import React, { useRef, useState, useEffect, useMemo } from 'react';
import './FitCultural.css';
import { ResponsivePie } from '@nivo/pie';

const calculatePercent = (value, total) => (total === 1 && value === 0 ? 25 : (value / total) * 100);

const getDataWithColors = (data, colors) => {
  const sortedIndices = data
    .map((item, idx) => ({ idx, value: item.value }))
    .sort((a, b) => b.value - a.value)
    .map((item, rank) => ({ index: item.idx, rank }));

  return data.map((item, idx) => {
    const rank = sortedIndices.find((entry) => entry.index === idx).rank;
    return { ...item, color: colors[rank] };
  });
};

const FitCultural = ({ culturalFitResult }) => {
  const { a, c, e, i } = culturalFitResult;

  const total = a + i + c + e || 1;
  const aPercent = calculatePercent(a, total);
  const iPercent = calculatePercent(i, total);
  const cPercent = calculatePercent(c, total);
  const ePercent = calculatePercent(e, total);

  const rawData = [
    { id: 'Analítico', value: aPercent },
    { id: 'Idealizador', value: iPercent },
    { id: 'Comunicador', value: cPercent },
    { id: 'Executor', value: ePercent },
  ];

  const colors = ['#0AC6BD', '#3BD1CA', '#6CDDD7', '#D8F6F5'];

  const dataWithColors = useMemo(() => getDataWithColors(rawData, colors), [rawData]);

  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const radius = Math.min(dimensions.width, dimensions.height) / 2.5;
  const center = {
    x: dimensions.width / 2,
    y: dimensions.height / 2,
  };

  const labelPositions = useMemo(() => {
    const total = dataWithColors.reduce((acc, item) => acc + item.value, 0);
    let startAngle = 0;

    const base = dataWithColors.map((item) => {
      const angle = (item.value / total) * 360;
      const midAngle = startAngle + angle / 2 - 90;
      startAngle += angle;
      const rad = (midAngle * Math.PI) / 180;

      return {
        label: item.id,
        x: center.x + Math.cos(rad) * (radius + 20),
        y: center.y + Math.sin(rad) * (radius + 20),
      };
    });

    const adjusted = [...base];
    const padding = 20;
    for (let i = 0; i < adjusted.length; i++) {
      for (let j = i + 1; j < adjusted.length; j++) {
        if (Math.abs(adjusted[i].y - adjusted[j].y) < padding) {
          adjusted[j].y += padding;
        }
      }
    }

    return adjusted;
  }, [dataWithColors, center, radius]);

  const renderTooltip = ({ datum }) => (
    <div
      style={{
        fontSize: '10px',
        fontWeight: 500,
        background: 'white',
        padding: '4px 6px',
        borderRadius: '4px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}
    >
      {datum.value.toFixed(1)}%
    </div>
  );

  return (
    <div className="fit-cultural-container">
      <div className="titleFitCulutal">Fit Cultural</div>
      <div className="chart-container">
        <div ref={containerRef} style={{ width: '100%', height: '182px', position: 'relative' }}>
          {/* Gráfico - camada 2 */}
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
            <ResponsivePie
              data={dataWithColors}
              innerRadius={0.5}
              padAngle={0}
              cornerRadius={0}
              activeOuterRadiusOffset={4}
              colors={({ data }) => data.color}
              borderWidth={0}
              margin={{ top: 5, right: 10, bottom: 5, left: 10 }}
              enableArcLabels={false}
              enableArcLinkLabels={false}
              tooltip={renderTooltip}
            />
          </div>

          {/* SVG com bordas - camada 1 (atrás do gráfico) */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {/* Borda externa — casando com outerRadius */}
            <circle
              cx="50%"
              cy="50%"
              r="33%"
              fill="none"
              stroke="white"
              strokeWidth="4"
            />

            {/* Borda interna — casando com innerRadius */}
            <circle
              cx="50%"
              cy="50%"
              r="15.5%"
              fill="none"
              stroke="white"
              strokeWidth="4"
            />
          </svg>

          {/* Labels — camada 3 (acima de tudo) */}
          {dimensions.width > 0 &&
            rawData.map((item, index) => {
              const total = rawData.reduce((acc, d) => acc + d.value, 0);
              const startAngle = rawData
                .slice(0, index)
                .reduce((acc, d) => acc + (d.value / total) * 360, 0);

              const angle = (item.value / total) * 360;
              const midAngle = startAngle + angle / 2 - 90;
              const rad = (midAngle * Math.PI) / 180;

              const x = center.x + Math.cos(rad) * (radius + 20);
              const y = center.y + Math.sin(rad) * (radius + 20);

              return (
                <div
                  key={item.id}
                  style={{
                    position: 'absolute',
                    left: x,
                    top: y,
                    transform: 'translate(-50%, -50%)',
                    background: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: 500,
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                    zIndex: 3,
                  }}
                >
                  {item.id}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default FitCultural;
